'use client';
import { MAIN_NAVIGATION } from '../../utils/websiteSsr/content';
import { parseCollection } from '../content/collections';
import { useLocation, useRevalidator } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getWebPath } from '../pathPrefix';
import { useState, useRef, useEffect } from 'react';
import { getFlagIconForLanguage } from '../../miniapp/i18n/flags';
import BrandLogo from '../../miniapp/ui-kit/BrandLogo';
import Icon from '../../miniapp/ui-kit/Icon';
import { useWebsiteAppContext } from '../hooks/useWebsiteAppContext';
import TransitionLink, { useTransitionNavigate } from '../client/TransitionLink';

interface HeaderProps {
  logoUrl: string | null;
}

function navButtonClass(isActive: boolean, display = 'flex'): string {
  return `${display} items-center justify-center border-0 rounded-circle w-9 h-9 sm:w-10 sm:h-10 shrink-0 cursor-pointer bg-secondary text-text transition-all hover:bg-secondary/80 ${isActive ? 'ring-2 ring-accent/40' : ''}`;
}

export default function Header({ logoUrl }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { cartCount, sessionUser, logout, ssr } = useWebsiteAppContext();
  const location = useLocation();
  const navigate = useTransitionNavigate();
  const revalidator = useRevalidator();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ka', name: 'ქართული' },
    { code: 'ru', name: 'Русский' },
  ];

  const currentLangCode = i18n.resolvedLanguage || i18n.language || 'en';
  const languageHref = (language: string) => {
    const params = new URLSearchParams({ lng: language });
    const collection = new URLSearchParams(location.search).get('collection');
    if (parseCollection(collection)) params.set('collection', collection!);
    return `${getWebPath(location.pathname)}?${params}${location.hash}`;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLangOpen(false);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <header className="sticky top-0 z-10 bg-bg/90 backdrop-blur-md border-b border-border">
      <div className="max-w-web mx-auto min-h-[var(--web-header-height)] px-[var(--web-side-padding)] py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-6 min-w-0">
          <TransitionLink
            to="/"
            className="inline-flex items-center no-underline shrink-0"
            aria-label={t('website.header.brandAria')}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="block h-10 w-auto max-w-[160px] object-contain sm:h-12 sm:max-w-[220px]"
              />
            ) : (
              <BrandLogo size="sm" className="sm:h-12" />
            )}
          </TransitionLink>
        </div>

        <nav
          aria-label={t('website.nav.account')}
          className="flex items-center gap-1.5 sm:gap-2 shrink-0"
        >
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={t(isMenuOpen ? 'website.nav.closeMenu' : 'website.nav.menu')}
            aria-expanded={isMenuOpen}
            aria-controls="site-navigation"
            className={`${navButtonClass(false)} lg:hidden`}
          >
            <Icon name={isMenuOpen ? 'close' : 'menu'} />
          </button>
          <div className="relative shrink-0" ref={langRef}>
            <button
              type="button"
              aria-label={t('website.nav.language')}
              aria-expanded={isLangOpen}
              aria-controls="site-languages"
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-full bg-secondary text-text text-sm font-medium hover:bg-secondary/80 transition-colors border-0 cursor-pointer whitespace-nowrap"
            >
              <Icon name="language" className="hidden sm:inline-block text-[20px]" />
              <span className="uppercase">{currentLangCode.split('-')[0]}</span>
            </button>
            <div
              id="site-languages"
              aria-hidden={!isLangOpen}
              className={`absolute top-full right-0 mt-1 bg-bg border border-border rounded-4xl shadow-lg overflow-hidden min-w-[140px] z-50 ${isLangOpen ? 'block' : 'hidden'}`}
            >
              {languages.map((lang) => (
                <a
                  key={lang.code}
                  href={languageHref(lang.code)}
                  hrefLang={lang.code}
                  lang={lang.code}
                  onClick={(event) => {
                    event.preventDefault();
                    void i18n.changeLanguage(lang.code).then(() => {
                      if (ssr) {
                        document.cookie = `tea_lng=${lang.code}; Path=/; Max-Age=31536000; SameSite=Lax`;
                        const params = new URLSearchParams(location.search);
                        if (params.has('lng')) {
                          params.delete('lng');
                          navigate(
                            {
                              pathname: location.pathname,
                              search: params.toString() ? `?${params}` : '',
                            },
                            { replace: true },
                          );
                        }
                        revalidator.revalidate();
                      }
                    });
                    setIsLangOpen(false);
                  }}
                  className={`w-full no-underline text-left px-4 py-2.5 text-sm hover:bg-secondary transition-colors flex items-center gap-2 border-0 bg-transparent cursor-pointer text-text ${
                    currentLangCode.startsWith(lang.code) ? 'bg-secondary font-semibold' : ''
                  }`}
                >
                  <span className="text-base leading-none">
                    {getFlagIconForLanguage(lang.code)}
                  </span>
                  <span>{lang.name}</span>
                </a>
              ))}
            </div>
          </div>

          <TransitionLink
            to="/cart"
            className={navButtonClass(location.pathname === '/cart')}
            aria-current={location.pathname === '/cart' ? 'page' : undefined}
            title={t('cart.title')}
          >
            <div className="relative flex items-center justify-center">
              <Icon name="shopping_cart" />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-circle bg-accent text-on-accent text-[10px] font-bold flex items-center justify-center px-1">
                  {cartCount}
                </span>
              )}
            </div>
          </TransitionLink>

          <TransitionLink
            to="/orders"
            className={navButtonClass(location.pathname === '/orders', 'hidden sm:flex')}
            aria-current={location.pathname === '/orders' ? 'page' : undefined}
            title={t('orders.title')}
          >
            <Icon name="receipt_long" />
          </TransitionLink>

          {(sessionUser?.isManager || sessionUser?.isCourier) && (
            <a
              className="border-0 rounded-full px-2.5 sm:px-4 py-1.5 sm:py-2 cursor-pointer bg-secondary text-text no-underline font-medium text-xs sm:text-sm hover:bg-secondary/80 transition-colors whitespace-nowrap shrink-0"
              href={getWebPath('/manager')}
            >
              {t('website.header.manager')}
            </a>
          )}

          {sessionUser ? (
            <>
              <TransitionLink
                to="/profile"
                className={navButtonClass(location.pathname === '/profile')}
                aria-current={location.pathname === '/profile' ? 'page' : undefined}
                title={t('settings.title')}
              >
                <Icon name="person" />
              </TransitionLink>
              <button
                type="button"
                className={navButtonClass(false, 'hidden sm:flex')}
                onClick={() => void logout()}
                title={t('website.header.logout')}
              >
                <Icon name="logout" />
              </button>
            </>
          ) : (
            <TransitionLink
              to="/login"
              className={navButtonClass(location.pathname === '/login')}
              aria-current={location.pathname === '/login' ? 'page' : undefined}
              title={t('website.header.login')}
            >
              <Icon name="login" />
            </TransitionLink>
          )}
        </nav>
      </div>
      <nav
        id="site-navigation"
        aria-label={t('website.nav.main')}
        className={`${isMenuOpen ? 'block' : 'hidden'} border-t border-border lg:block`}
      >
        <ul className="mx-auto my-0 grid max-w-web list-none grid-cols-2 gap-1 px-[var(--web-side-padding)] py-2 lg:flex lg:flex-wrap lg:gap-x-2">
          {MAIN_NAVIGATION.map((item) => (
            <li key={item.key}>
              <TransitionLink
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                aria-current={location.pathname === item.path ? 'page' : undefined}
                className={`flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-semibold transition-colors hover:bg-secondary ${location.pathname === item.path ? 'bg-secondary text-text' : 'text-text-secondary'}`}
              >
                {t(`website.nav.${item.key}`)}
              </TransitionLink>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
}
